import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Loader2,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Wallet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import {
  createCarRentalRow,
  deleteCarRentalRow,
  fetchAllCarRentals,
  getCarRentalErrorMessage,
  readCarRentalsSessionCache,
  writeCarRentalsSessionCache,
  STATUS_VALUES,
  upsertCarRentalRow
} from '../services/carRentalsService';
import {
  loadCarRentalSuggestions,
  mergeSuggestionsFromRows,
  rememberCarRentalField,
  rememberCarRentalRow
} from '../utils/carRentalSuggestions';
import './CarRentalInvoicing.css';

const ITEMS_PER_PAGE = 10;

const YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028];
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const formatAmount = (n) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseAmountInput = (raw) => {
  const cleaned = String(raw ?? '').replace(/,/g, '').trim();
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const statusClass = (status) => {
  switch (status) {
    case 'Paid':
      return 'cri-status--paid';
    case 'Processed':
      return 'cri-status--processed';
    case 'Pending':
      return 'cri-status--pending';
    case 'Waiting for PO':
      return 'cri-status--waiting';
    default:
      return '';
  }
};

/** Palette for rows that share the same project + invoice number. */
const PROJECT_INVOICE_RIBBON_COLORS = [
  '#10b981',
  '#f59e0b',
  '#185fa5',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#854f0b',
  '#3b6d11'
];

const getProjectInvoiceGroupKey = (row) => {
  const project = String(row?.project ?? '').trim();
  const invoice = String(row?.invoiceNumber ?? '').trim();
  if (!project || !invoice) return '';
  return `${project.toLowerCase()}|${invoice.toLowerCase()}`;
};

const periodCacheKey = (year, month) => `${year}-${month}`;

const filterRowsByPeriod = (allRows, year, month) =>
  allRows.filter((r) => r.year === year && r.month === month);

const sortPeriodRows = (rows) =>
  [...rows].sort((a, b) => {
    const p = String(a.project || '').localeCompare(String(b.project || ''), undefined, {
      sensitivity: 'base'
    });
    if (p !== 0) return p;
    return String(a.regNumber || '').localeCompare(String(b.regNumber || ''), undefined, {
      sensitivity: 'base'
    });
  });

const buildPeriodCache = (allRows) => {
  const map = new Map();
  allRows.forEach((row) => {
    const key = periodCacheKey(row.year, row.month);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  });
  map.forEach((rows, key) => map.set(key, sortPeriodRows(rows)));
  return map;
};

const normalizeRegNumber = (value) => String(value ?? '').trim().toUpperCase();

/** Fields copied from the same reg number in an earlier month. */
const PREV_MONTH_AUTOFILL_FIELDS = ['project', 'userName', 'rentalCompany'];

/** Most recent row for this reg in any month before the selected period. */
const findRowByRegInPriorMonths = (allRows, year, month, regNumber, excludeRowId) => {
  const key = normalizeRegNumber(regNumber);
  if (!key) return null;

  const currentPeriod = year * 12 + month;
  const matches = allRows
    .filter((row) => {
      if (row.id === excludeRowId) return false;
      if (normalizeRegNumber(row.regNumber) !== key) return false;
      const rowPeriod = (row.year || 0) * 12 + (row.month || 0);
      return rowPeriod < currentPeriod;
    })
    .sort((a, b) => {
      const periodA = (a.year || 0) * 12 + (a.month || 0);
      const periodB = (b.year || 0) * 12 + (b.month || 0);
      return periodB - periodA;
    });

  return matches[0] || null;
};

const buildAutofillFromPreviousMonth = (sourceRow) => {
  const patch = {};
  PREV_MONTH_AUTOFILL_FIELDS.forEach((field) => {
    patch[field] = sourceRow[field];
  });
  return patch;
};

/** Patch to copy prior-month row data when reg number matches exactly. */
const getRegAutofillPatch = (allRows, year, month, rowId, regNumber, rowBeforePatch) => {
  const key = normalizeRegNumber(regNumber);
  if (!key) return null;

  const source = findRowByRegInPriorMonths(allRows, year, month, regNumber, rowId);
  if (!source) return null;

  const previousKey = normalizeRegNumber(rowBeforePatch?.regNumber);
  const rowStillEmpty =
    !String(rowBeforePatch?.project ?? '').trim() &&
    !String(rowBeforePatch?.userName ?? '').trim() &&
    !String(rowBeforePatch?.rentalCompany ?? '').trim();

  if (!rowStillEmpty && key === previousKey) return null;

  return {
    ...buildAutofillFromPreviousMonth(source),
    regNumber: String(regNumber ?? '').trim()
  };
};

/** True if another row in the same period already uses this reg number. */
const isDuplicateRegInRows = (allRows, rowId, regNumber) => {
  const key = normalizeRegNumber(regNumber);
  if (!key) return false;
  return allRows.some((r) => r.id !== rowId && normalizeRegNumber(r.regNumber) === key);
};

/** Stored display (dd/mm/yyyy) → native date input value (yyyy-mm-dd). */
const datePaidToInputValue = (stored) => {
  const s = String(stored ?? '').trim();
  if (!s) return '';
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return `${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
};

const CRI_COLUMN_LABELS = {
  project: 'Project',
  userName: 'User name',
  regNumber: 'Reg number',
  rentalCompany: 'Rental company',
  amount: 'Amount to be paid',
  poNumber: 'PO No.',
  invoiceNumber: 'Invoice No.',
  status: 'Status',
  datePaid: 'Date paid',
  comment: 'Comment'
};

const CRI_COL_ORDER = [
  'project',
  'userName',
  'regNumber',
  'rentalCompany',
  'amount',
  'poNumber',
  'invoiceNumber',
  'status',
  'datePaid',
  'comment'
];

/** Min / max px per column — table adapts to longest value within these bounds. */
const CRI_COL_MIN = {
  project: 68,
  userName: 76,
  regNumber: 84,
  rentalCompany: 96,
  amount: 108,
  poNumber: 96,
  invoiceNumber: 96,
  status: 108,
  datePaid: 118,
  comment: 72,
  delete: 32
};
/** Only comment is capped; all other columns grow to fit the longest value. */
const CRI_COMMENT_MAX = 200;

const CHAR_WIDTH_PX = 7.5;
/** PO / invoice values are often numeric — allow slightly wider measure so nothing clips. */
const CHAR_WIDTH_PO_INVOICE_PX = 8.5;
const CELL_H_PAD = 32;
const RIBBON_EXTRA_PX = 14;

const measureTextPx = (text, columnKey) => {
  const factor =
    columnKey === 'poNumber' || columnKey === 'invoiceNumber'
      ? CHAR_WIDTH_PO_INVOICE_PX
      : CHAR_WIDTH_PX;
  return Math.ceil(String(text ?? '').length * factor);
};

const computeCarRentalStats = (dataRows) => {
  let totalInvoiced = 0;
  let paid = 0;
  let processed = 0;
  let pendingWaiting = 0;
  dataRows.forEach((r) => {
    const amt = Number(r.amount) || 0;
    totalInvoiced += amt;
    if (r.status === 'Paid') paid += amt;
    else if (r.status === 'Processed') processed += amt;
    else if (r.status === 'Pending' || r.status === 'Waiting for PO') pendingWaiting += amt;
  });
  return { totalInvoiced, paid, processed, pendingWaiting };
};

const rowMatchesSearch = (row, query) => {
  if (!query.trim()) return true;
  const q = query.toLowerCase().trim();
  const haystack = [
    row.project,
    row.userName,
    row.regNumber,
    row.rentalCompany,
    row.amount,
    row.poNumber,
    row.invoiceNumber,
    row.status,
    row.datePaid,
    row.comment
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ');
  return haystack.includes(q);
};

/** Column widths from longest header label or cell value in the current month. */
const computeCarRentalColumnWidths = (dataRows, amountFocusId) => {
  const widths = { delete: CRI_COL_MIN.delete };
  Object.entries(CRI_COLUMN_LABELS).forEach(([key, label]) => {
    let maxPx = measureTextPx(label, key);
    if (key === 'status') {
      STATUS_VALUES.forEach((s) => {
        maxPx = Math.max(maxPx, measureTextPx(s, key));
      });
    }
    dataRows.forEach((row) => {
      let display = '';
      if (key === 'amount') {
        display =
          amountFocusId === row.id
            ? String(row.amount === 0 ? '' : row.amount)
            : formatAmount(row.amount);
      } else {
        display = row[key];
      }
      maxPx = Math.max(maxPx, measureTextPx(display, key));
    });
    const ribbonExtra = key === 'project' || key === 'invoiceNumber' ? RIBBON_EXTRA_PX : 0;
    const total = maxPx + CELL_H_PAD + ribbonExtra;
    if (key === 'comment') {
      widths[key] = Math.min(CRI_COMMENT_MAX, Math.max(CRI_COL_MIN.comment, total));
    } else {
      widths[key] = Math.max(CRI_COL_MIN[key] ?? 64, total);
    }
  });
  return widths;
};

/** Native date input → dd/mm/yyyy for storage and display. */
const inputValueToDatePaid = (iso) => {
  const s = String(iso ?? '').trim();
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length === 3) {
    return `${parseInt(parts[2], 10)}/${parseInt(parts[1], 10)}/${parts[0]}`;
  }
  return s;
};

function CriSuggestInput({ listId, className = 'cri-cell-input', ...inputProps }) {
  return <input className={className} list={listId} autoComplete="off" {...inputProps} />;
}

/** One datalist per column — must not be duplicated inside each table row. */
function CriSuggestionLists({ suggestions }) {
  const lists = [
    { id: 'cri-dl-project', items: suggestions.project },
    { id: 'cri-dl-user-name', items: suggestions.userName },
    { id: 'cri-dl-reg-number', items: suggestions.regNumber },
    { id: 'cri-dl-rental-company', items: suggestions.rentalCompany },
    { id: 'cri-dl-po-number', items: suggestions.poNumber },
    { id: 'cri-dl-invoice-number', items: suggestions.invoiceNumber }
  ];
  return (
    <>
      {lists.map(({ id, items }) => (
        <datalist key={id} id={id}>
          {(items || []).map((val) => (
            <option key={val} value={val} />
          ))}
        </datalist>
      ))}
    </>
  );
}

/** Project / Invoice cells with a left colour ribbon when the pair is shared by 2+ rows. */
function CriRibbonCell({ ribbonColor, tdClassName, children }) {
  return (
    <td className={`cri-td-data ${tdClassName || ''}`.trim()}>
      <div
        className={`cri-ribbon-cell${ribbonColor ? ' cri-ribbon-cell--active' : ''}`}
        style={ribbonColor ? { '--cri-ribbon-color': ribbonColor } : undefined}
      >
        {ribbonColor ? (
          <span
            className="cri-group-ribbon"
            style={{ backgroundColor: ribbonColor }}
            aria-hidden="true"
            title="Same project and invoice as other row(s)"
          />
        ) : null}
        <div className="cri-ribbon-cell-body">{children}</div>
      </div>
    </td>
  );
}

function getPageNumbers(totalPages, safePage) {
  const pageNumbers = [];
  const maxVisiblePages = 5;
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else if (safePage <= 3) {
    for (let i = 1; i <= 4; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  } else if (safePage >= totalPages - 2) {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    pageNumbers.push('...');
    for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
    pageNumbers.push('...');
    pageNumbers.push(totalPages);
  }
  return pageNumbers;
}

function CriPagination({ totalFiltered, safePage, totalPages, indexOfFirstItem, indexOfLastItem, onPageChange }) {
  if (totalFiltered <= 0) return null;

  const goToPage = (page) => {
    onPageChange(page);
    const wrapper = document.querySelector('.cri-table-wrapper');
    if (wrapper) {
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  return (
    <div className="cri-pagination">
      <div className="cri-pagination__info">
        Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
      </div>
      <div className="cri-pagination__controls">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cri-soft-btn h-8 w-8"
          onClick={() => safePage > 1 && goToPage(safePage - 1)}
          disabled={safePage === 1}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="cri-pagination__numbers">
          {getPageNumbers(totalPages, safePage).map((pageNum, index) =>
            pageNum === '...' ? (
              <span key={`ellipsis-${index}`} className="cri-pagination__ellipsis">
                …
              </span>
            ) : (
              <button
                key={pageNum}
                type="button"
                className={`cri-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </button>
            )
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="cri-soft-btn h-8 w-8"
          onClick={() => safePage < totalPages && goToPage(safePage + 1)}
          disabled={safePage === totalPages}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function CarRentalInvoicing() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filterProject, setFilterProject] = useState('All');
  const [filterCompany, setFilterCompany] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [amountFocusId, setAmountFocusId] = useState(null);
  const [suggestions, setSuggestions] = useState(() => loadCarRentalSuggestions());
  const [currentPage, setCurrentPage] = useState(1);
  const saveTimers = useRef({});
  const rowSnapshotsRef = useRef({});
  const rowsLoadedRef = useRef(false);
  const rowsRef = useRef([]);
  const lastSavedPoByRowIdRef = useRef({});
  const layoutRowsRef = useRef([]);
  const [layoutTick, setLayoutTick] = useState(0);
  const periodCacheRef = useRef(new Map());
  const allRowsRef = useRef([]);
  const hasLoadedOnceRef = useRef(false);
  const selectedYearRef = useRef(selectedYear);
  const selectedMonthRef = useRef(selectedMonth);

  useEffect(() => {
    selectedYearRef.current = selectedYear;
  }, [selectedYear]);

  useEffect(() => {
    selectedMonthRef.current = selectedMonth;
  }, [selectedMonth]);

  const syncAllRowsStore = useCallback((allRows) => {
    allRowsRef.current = allRows;
    periodCacheRef.current = buildPeriodCache(allRows);
    writeCarRentalsSessionCache(allRows);
  }, []);

  const syncPeriodInStore = useCallback(
    (periodRows) => {
      const y = selectedYearRef.current;
      const m = selectedMonthRef.current;
      const key = periodCacheKey(y, m);
      const sorted = sortPeriodRows(periodRows);
      periodCacheRef.current.set(key, sorted);
      const others = allRowsRef.current.filter((r) => r.year !== y || r.month !== m);
      const all = [...others, ...sorted];
      allRowsRef.current = all;
      writeCarRentalsSessionCache(all);
    },
    []
  );

  const applyRowsToState = useCallback((data) => {
    const snapshots = {};
    const savedPos = {};
    data.forEach((row) => {
      snapshots[row.id] = row;
      savedPos[row.id] = String(row.poNumber || '').trim();
    });
    rowSnapshotsRef.current = snapshots;
    lastSavedPoByRowIdRef.current = savedPos;
    setRows(data);
    rowsRef.current = data;
    layoutRowsRef.current = data;
    setLayoutTick((t) => t + 1);
    rowsLoadedRef.current = true;
    queueMicrotask(() => {
      setSuggestions((prev) => mergeSuggestionsFromRows(prev, data));
    });
  }, []);

  const applySelectedPeriod = useCallback(() => {
    const y = selectedYearRef.current;
    const m = selectedMonthRef.current;
    const key = periodCacheKey(y, m);
    const periodRows =
      periodCacheRef.current.get(key) ?? filterRowsByPeriod(allRowsRef.current, y, m);
    applyRowsToState(periodRows);
  }, [applyRowsToState]);

  const clearPendingSaves = useCallback(() => {
    Object.values(saveTimers.current).forEach(clearTimeout);
    saveTimers.current = {};
  }, []);

  const loadAllRows = useCallback(async () => {
    setError(null);
    clearPendingSaves();

    const sessionCached = readCarRentalsSessionCache();
    if (sessionCached?.length) {
      syncAllRowsStore(sessionCached);
      applySelectedPeriod();
      setLoading(false);
      setRefreshing(true);
    } else if (!hasLoadedOnceRef.current) {
      rowSnapshotsRef.current = {};
      rowsLoadedRef.current = false;
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const data = await fetchAllCarRentals();
      syncAllRowsStore(data);
      applySelectedPeriod();
    } catch (err) {
      console.error(err);
      if (!sessionCached?.length) {
        setError(
          `Failed to load invoices: ${getCarRentalErrorMessage(err)}. Check Appwrite connection and run npm run setup:car-rentals.`
        );
        setRows([]);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
      hasLoadedOnceRef.current = true;
    }
  }, [clearPendingSaves, syncAllRowsStore, applySelectedPeriod]);

  useEffect(() => {
    loadAllRows();
  }, [loadAllRows]);

  /** Month/year switches use in-memory data — no Appwrite round-trip. */
  useEffect(() => {
    if (!hasLoadedOnceRef.current) return;
    setError(null);
    clearPendingSaves();
    applySelectedPeriod();
  }, [selectedYear, selectedMonth, clearPendingSaves, applySelectedPeriod]);

  useEffect(() => clearPendingSaves, [clearPendingSaves]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  /** Debounce column width recalc while editing — skip during fetch. */
  useEffect(() => {
    if (loading || refreshing) return;
    const timer = setTimeout(() => {
      layoutRowsRef.current = rows;
      setLayoutTick((t) => t + 1);
    }, 450);
    return () => clearTimeout(timer);
  }, [rows, loading, refreshing]);

  const projectInvoiceRibbonColors = useMemo(() => {
    const counts = new Map();
    rows.forEach((r) => {
      const key = getProjectInvoiceGroupKey(r);
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });
    const colorByKey = new Map();
    let idx = 0;
    counts.forEach((count, key) => {
      if (count >= 2) {
        colorByKey.set(key, PROJECT_INVOICE_RIBBON_COLORS[idx % PROJECT_INVOICE_RIBBON_COLORS.length]);
        idx += 1;
      }
    });
    return colorByKey;
  }, [rows]);

  const duplicateRegRowIds = useMemo(() => {
    const idsByKey = new Map();
    rows.forEach((r) => {
      const key = normalizeRegNumber(r.regNumber);
      if (!key) return;
      if (!idsByKey.has(key)) idsByKey.set(key, []);
      idsByKey.get(key).push(r.id);
    });
    const dupes = new Set();
    idsByKey.forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => dupes.add(id));
    });
    return dupes;
  }, [rows]);

  const duplicateRegCount = duplicateRegRowIds.size;

  useEffect(() => {
    if (duplicateRegRowIds.size === 0) {
      setError((prev) =>
        prev && prev.includes('Duplicate registration number') ? null : prev
      );
    }
  }, [duplicateRegRowIds]);

  const projectOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const p = String(r.project || '').trim();
      if (p) set.add(p);
    });
    return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const companyOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      const raw = String(r.rentalCompany || '').trim();
      if (!raw) return;
      const n = raw.toLowerCase().replace(/\s+/g, ' ');
      const c =
        /^smiles?(\s+rent[\s-]*a[\s-]*car)?$/.test(n) || (n.includes('smile') && n.includes('rent'))
          ? 'Smiles'
          : raw;
      set.add(c);
    });
    return ['All', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const filtersActive =
    filterProject !== 'All' ||
    filterCompany !== 'All' ||
    filterStatus !== 'All' ||
    searchQuery.trim() !== '';

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterProject !== 'All' && r.project !== filterProject) return false;
      if (filterCompany !== 'All') {
        const raw = String(r.rentalCompany || '').trim();
        const n = raw.toLowerCase().replace(/\s+/g, ' ');
        const company =
          /^smiles?(\s+rent[\s-]*a[\s-]*car)?$/.test(n) || (n.includes('smile') && n.includes('rent'))
            ? 'Smiles'
            : raw;
        if (company !== filterCompany) return false;
      }
      if (filterStatus !== 'All' && r.status !== filterStatus) return false;
      if (!rowMatchesSearch(r, searchQuery)) return false;
      return true;
    });
  }, [rows, filterProject, filterCompany, filterStatus, searchQuery]);

  const stats = useMemo(() => computeCarRentalStats(filteredRows), [filteredRows]);

  const filteredTotal = useMemo(
    () => filteredRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [filteredRows]
  );

  const colWidths = useMemo(
    () => computeCarRentalColumnWidths(layoutRowsRef.current, amountFocusId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layoutTick is a manual recompute trigger; layoutRowsRef itself is not reactive.
    [layoutTick, amountFocusId]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterProject, filterCompany, filterStatus, searchQuery, selectedYear, selectedMonth]);

  const totalFiltered = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * ITEMS_PER_PAGE + 1 : 0;
  const indexOfLastItem = Math.min(safePage * ITEMS_PER_PAGE, totalFiltered);
  const paginatedRows = useMemo(
    () => filteredRows.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE),
    [filteredRows, safePage]
  );

  const scheduleSave = useCallback((rowId) => {
    if (!rowId || !rowsLoadedRef.current) return;
    if (saveTimers.current[rowId]) clearTimeout(saveTimers.current[rowId]);
    saveTimers.current[rowId] = setTimeout(async () => {
      delete saveTimers.current[rowId];
      const snapshot = rowSnapshotsRef.current[rowId];
      if (!snapshot?.id) return;
      if (isDuplicateRegInRows(rowsRef.current, rowId, snapshot.regNumber)) {
        setError(
          'Duplicate registration number in this month. Each reg number can only be used once.'
        );
        return;
      }
      try {
        const previousPoNumber = lastSavedPoByRowIdRef.current[rowId] ?? snapshot.poNumber;
        const saved = await upsertCarRentalRow(rowId, snapshot, { previousPoNumber });
        lastSavedPoByRowIdRef.current[rowId] = saved.poNumber || '';
        const live = rowSnapshotsRef.current[rowId];
        // Never overwrite in-progress typing with a stale server response.
        const merged = live ? { ...saved, ...live, id: rowId } : saved;
        rowSnapshotsRef.current[rowId] = merged;
        setRows((prev) => {
          const next = prev.map((r) => (r.id === rowId ? merged : r));
          rowsRef.current = next;
          syncPeriodInStore(next);
          return next;
        });
        rememberCarRentalRow(saved);
        setError((prev) =>
          prev && (prev.startsWith('Failed to save') || prev.startsWith('Failed to add'))
            ? null
            : prev
        );
      } catch (err) {
        console.error(err);
        setError(`Failed to save: ${getCarRentalErrorMessage(err)}`);
      }
    }, 600);
  }, [syncPeriodInStore]);

  const updateRow = useCallback(
    (id, patch) => {
      setRows((prev) => {
        const current = prev.find((r) => r.id === id);
        if (!current) return prev;

        let mergedPatch = { ...patch };
        if ('regNumber' in patch) {
          const autofillPatch = getRegAutofillPatch(
            allRowsRef.current,
            selectedYearRef.current,
            selectedMonthRef.current,
            id,
            patch.regNumber,
            current
          );
          if (autofillPatch) {
            mergedPatch = autofillPatch;
          }
        }

        const next = prev.map((r) => (r.id === id ? { ...r, ...mergedPatch } : r));
        const updated = next.find((r) => r.id === id);
        if (updated) {
          rowSnapshotsRef.current[id] = updated;
          scheduleSave(id);
        }
        return next;
      });
    },
    [scheduleSave]
  );

  const handleAddRow = async () => {
    try {
      const created = await createCarRentalRow({
        project: '',
        userName: '',
        regNumber: '',
        rentalCompany: '',
        amount: 0,
        poNumber: '',
        invoiceNumber: '',
        status: 'Pending',
        datePaid: '',
        comment: '',
        month: selectedMonth,
        year: selectedYear
      });
      rowSnapshotsRef.current[created.id] = created;
      lastSavedPoByRowIdRef.current[created.id] = String(created.poNumber || '').trim();
      setRows((prev) => {
        const next = [...prev, created];
        syncPeriodInStore(next);
        return next;
      });
    } catch (err) {
      console.error(err);
      setError(`Failed to add row: ${getCarRentalErrorMessage(err)}`);
    }
  };

  const handleDeleteRow = async (id) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      const poToResync =
        lastSavedPoByRowIdRef.current[id] ||
        String(rowSnapshotsRef.current[id]?.poNumber || '').trim();
      await deleteCarRentalRow(id);
      if (saveTimers.current[id]) {
        clearTimeout(saveTimers.current[id]);
        delete saveTimers.current[id];
      }
      delete rowSnapshotsRef.current[id];
      delete lastSavedPoByRowIdRef.current[id];
      if (poToResync) {
        const { scheduleFleetPoSyncFromCarRental } = await import(
          '../services/fleetPoCarRentalSync'
        );
        scheduleFleetPoSyncFromCarRental(poToResync);
      }
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        syncPeriodInStore(next);
        return next;
      });
    } catch (err) {
      console.error(err);
      setError(`Failed to delete row: ${getCarRentalErrorMessage(err)}`);
    }
  };

  const clearFilters = () => {
    setFilterProject('All');
    setFilterCompany('All');
    setFilterStatus('All');
    setSearchQuery('');
  };

  const applyExportFilters = useCallback(
    (dataRows) =>
      dataRows.filter((r) => {
        if (filterProject !== 'All' && r.project !== filterProject) return false;
        if (filterCompany !== 'All' && r.rentalCompany !== filterCompany) return false;
        if (filterStatus !== 'All' && r.status !== filterStatus) return false;
        if (!rowMatchesSearch(r, searchQuery)) return false;
        return true;
      }),
    [filterProject, filterCompany, filterStatus, searchQuery]
  );

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    for (let month = 1; month <= 12; month += 1) {
      const monthRows = sortPeriodRows(
        applyExportFilters(allRowsRef.current.filter((r) => r.year === selectedYear && r.month === month))
      );
      const sheetRows = monthRows.map((r) => ({
        Project: r.project,
        'User Name': r.userName,
        'Reg Number': r.regNumber,
        'Rental Company': r.rentalCompany,
        'Amount to be Paid': Number(r.amount) || 0,
        'PO No.': r.poNumber,
        'Invoice No.': r.invoiceNumber,
        Status: r.status,
        'Date Paid': r.datePaid,
        Comment: r.comment
      }));
      const totalAmount = monthRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
      sheetRows.push({
        Project: '',
        'User Name': '',
        'Reg Number': '',
        'Rental Company': 'TOTAL',
        'Amount to be Paid': totalAmount,
        'PO No.': '',
        'Invoice No.': '',
        Status: '',
        'Date Paid': '',
        Comment: ''
      });
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      XLSX.utils.book_append_sheet(wb, ws, MONTH_SHORT[month - 1]);
    }
    XLSX.writeFile(wb, `Car_Rental_Invoicing_${selectedYear}.xlsx`);
  };

  const summaryCards = [
    {
      id: 'total',
      label: `Total invoiced${filtersActive ? ' · filtered' : ''}`,
      value: formatAmount(stats.totalInvoiced),
      accent: '#3b82f6',
      icon: Wallet
    },
    {
      id: 'paid',
      label: `Paid${filtersActive ? ' · filtered' : ''}`,
      value: formatAmount(stats.paid),
      accent: '#10b981',
      icon: CheckCircle2
    },
    {
      id: 'processed',
      label: `Processed${filtersActive ? ' · filtered' : ''}`,
      value: formatAmount(stats.processed),
      accent: '#8b5cf6',
      icon: Loader2
    },
    {
      id: 'pending',
      label: `Pending / waiting${filtersActive ? ' · filtered' : ''}`,
      value: formatAmount(stats.pendingWaiting),
      accent: '#f59e0b',
      icon: Clock
    }
  ];

  return (
    <div className="cri-page">
      <CriSuggestionLists suggestions={suggestions} />
      <div className="cri-scroll">
        <div className="cri-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Fleet', to: '/sagemcom/fleet-management', hideOnMobile: true },
              { label: 'Car rental invoicing' }
            ]}
          />

          <section className="cri-intro" aria-label="Car rental invoicing overview">
            <div className="cri-intro-text">
              <h1 className="cri-intro-title">Car rental invoicing</h1>
              <p className="cri-intro-subtitle">
                Monthly invoice tracking by project, vehicle and rental company
              </p>
            </div>
            <div className="cri-intro-actions">
              <select
                className="cri-period-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                aria-label="Year"
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                className="cri-period-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                aria-label="Month"
              >
                {MONTH_LABELS.map((label, i) => (
                  <option key={label} value={i + 1}>
                    {MONTH_SHORT[i]}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                className="cri-export-btn h-9 gap-1.5"
                onClick={handleExport}
                disabled={loading || refreshing}
              >
                <Download className="h-3.5 w-3.5" />
                Export Excel
              </Button>
            </div>
          </section>

          <div className="cri-summary-cards">
            {summaryCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.id} className="cri-summary-card" style={{ '--cat-accent': card.accent }}>
                  <div className="cri-summary-card__top">
                    <span className="cri-summary-card__icon" aria-hidden>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="cri-summary-card__label">{card.label}</span>
                  </div>
                  <div className="cri-summary-card__amount">{card.value}</div>
                </div>
              );
            })}
          </div>

          {error ? (
            <div className="cri-error" role="alert">
              {error}
            </div>
          ) : null}

          <div className="cri-table-section">
            <div className="cri-table-section__head">
              <h2>
                Invoice entries
                <span className="cri-table-section__count">{filteredRows.length}</span>
              </h2>
              <Button
                type="button"
                className="cri-create-btn h-9 gap-1.5"
                onClick={handleAddRow}
                disabled={loading || refreshing}
              >
                <Plus className="h-4 w-4" />
                Add row
              </Button>
            </div>

            <div className="cri-toolbar">
              <div className="cri-search">
                <Search className="cri-search__icon h-4 w-4" aria-hidden />
                <Input
                  className="cri-search__input"
                  type="search"
                  placeholder="Search project, user, reg, PO, invoice…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search invoice entries"
                />
              </div>
              <select
                className="cri-filter-select"
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                aria-label="Project"
              >
                {projectOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === 'All' ? 'All projects' : opt}
                  </option>
                ))}
              </select>
              <select
                className="cri-filter-select"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                aria-label="Rental company"
              >
                {companyOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === 'All' ? 'All companies' : opt}
                  </option>
                ))}
              </select>
              <select
                className="cri-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                aria-label="Status"
              >
                <option value="All">All statuses</option>
                {STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <div className="cri-toolbar__spacer" aria-hidden="true" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="cri-soft-btn h-9 gap-1.5"
                onClick={clearFilters}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Clear
              </Button>
              {duplicateRegCount > 0 && (
                <span className="cri-duplicate-reg-badge" role="status">
                  {duplicateRegCount} duplicate reg {duplicateRegCount === 1 ? 'number' : 'numbers'}
                </span>
              )}
              {filtersActive && (
                <span className="cri-filter-count">
                  {filteredRows.length} of {rows.length} entries shown
                </span>
              )}
            </div>

            {loading ? (
              <div className="cri-loading" role="status">
                Loading invoice entries…
              </div>
            ) : (
              <>
                <div className="cri-table-shell">
                  {refreshing && (
                    <div className="cri-table-refresh-bar" aria-live="polite">
                      Updating…
                    </div>
                  )}
                  <div className="cri-table-wrapper">
                    <table className="cri-table">
                      <colgroup>
                        {CRI_COL_ORDER.map((key) => (
                          <col key={key} style={{ width: colWidths[key] }} />
                        ))}
                        <col style={{ width: colWidths.delete }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="cri-th-project">Project</th>
                          <th className="cri-th-user-name">User name</th>
                          <th className="cri-th-reg-number">Reg number</th>
                          <th>Rental company</th>
                          <th className="cri-th-amount">Amount to be paid</th>
                          <th>PO No.</th>
                          <th className="cri-th-invoice">Invoice No.</th>
                          <th>Status</th>
                          <th>Date paid</th>
                          <th>Comment</th>
                          <th aria-label="Delete" />
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.length === 0 ? (
                          <tr>
                            <td colSpan={11} className="cri-empty-row">
                              No entries for this period. Click &quot;Add row&quot; to create one.
                            </td>
                          </tr>
                        ) : (
                          paginatedRows.map((row) => {
                            const groupKey = getProjectInvoiceGroupKey(row);
                            const ribbonColor = groupKey
                              ? projectInvoiceRibbonColors.get(groupKey) ?? null
                              : null;
                            const isRegDuplicate = duplicateRegRowIds.has(row.id);
                            return (
                              <tr key={row.id} className={isRegDuplicate ? 'cri-row-reg-duplicate' : undefined}>
                                <CriRibbonCell ribbonColor={ribbonColor} tdClassName="cri-td-project">
                                  <CriSuggestInput
                                    listId="cri-dl-project"
                                    className="cri-cell-input cri-cell-project"
                                    value={row.project}
                                    onChange={(e) => updateRow(row.id, { project: e.target.value })}
                                    onBlur={(e) => {
                                      const store = rememberCarRentalField('project', e.target.value);
                                      if (store) setSuggestions(store);
                                    }}
                                  />
                                </CriRibbonCell>
                                <td className="cri-td-data cri-td-user-name">
                                  <CriSuggestInput
                                    listId="cri-dl-user-name"
                                    className="cri-cell-input cri-cell-user-name"
                                    value={row.userName}
                                    onChange={(e) => updateRow(row.id, { userName: e.target.value })}
                                    onBlur={(e) => {
                                      const store = rememberCarRentalField('userName', e.target.value);
                                      if (store) setSuggestions(store);
                                    }}
                                  />
                                </td>
                                <td className={`cri-td-data cri-td-reg-number${isRegDuplicate ? ' cri-td-reg-number--duplicate' : ''}`}>
                                  <CriSuggestInput
                                    listId="cri-dl-reg-number"
                                    className={`cri-cell-input cri-cell-reg-number${isRegDuplicate ? ' cri-cell-input--duplicate' : ''}`}
                                    value={row.regNumber}
                                    onChange={(e) => updateRow(row.id, { regNumber: e.target.value })}
                                    onBlur={(e) => {
                                      const value = e.target.value;
                                      const store = rememberCarRentalField('regNumber', value);
                                      if (store) setSuggestions(store);
                                      // Datalist selection often commits after blur — retry autofill once.
                                      window.setTimeout(() => {
                                        updateRow(row.id, { regNumber: value });
                                      }, 0);
                                      if (isDuplicateRegInRows(rowsRef.current, row.id, value)) {
                                        setError(
                                          'Duplicate registration number in this month. Each reg number can only be used once.'
                                        );
                                      }
                                    }}
                                    title={
                                      isRegDuplicate
                                        ? 'Duplicate registration number — used on another row this month'
                                        : 'Type or pick a reg number — project, user name and rental company fill from the previous month'
                                    }
                                    aria-invalid={isRegDuplicate || undefined}
                                  />
                                </td>
                                <td className="cri-td-data">
                                  <CriSuggestInput
                                    listId="cri-dl-rental-company"
                                    value={row.rentalCompany}
                                    onChange={(e) => updateRow(row.id, { rentalCompany: e.target.value })}
                                    onBlur={(e) => {
                                      const store = rememberCarRentalField('rentalCompany', e.target.value);
                                      if (store) setSuggestions(store);
                                    }}
                                  />
                                </td>
                                <td className="cri-td-data">
                                  <input
                                    className="cri-cell-input cri-cell-amount"
                                    value={
                                      amountFocusId === row.id
                                        ? String(row.amount === 0 ? '' : row.amount)
                                        : formatAmount(row.amount)
                                    }
                                    onFocus={() => setAmountFocusId(row.id)}
                                    onChange={(e) =>
                                      updateRow(row.id, { amount: parseAmountInput(e.target.value) })
                                    }
                                    onBlur={() => setAmountFocusId(null)}
                                  />
                                </td>
                                <td className="cri-td-data cri-td-po">
                                  <CriSuggestInput
                                    listId="cri-dl-po-number"
                                    className="cri-cell-input cri-cell-po"
                                    value={row.poNumber}
                                    title={row.poNumber || undefined}
                                    onChange={(e) => updateRow(row.id, { poNumber: e.target.value })}
                                    onBlur={(e) => {
                                      const store = rememberCarRentalField('poNumber', e.target.value);
                                      if (store) setSuggestions(store);
                                    }}
                                  />
                                </td>
                                <CriRibbonCell ribbonColor={ribbonColor} tdClassName="cri-td-invoice">
                                  <CriSuggestInput
                                    listId="cri-dl-invoice-number"
                                    className="cri-cell-input cri-cell-invoice"
                                    value={row.invoiceNumber}
                                    title={row.invoiceNumber || undefined}
                                    onChange={(e) => updateRow(row.id, { invoiceNumber: e.target.value })}
                                    onBlur={(e) => {
                                      const store = rememberCarRentalField('invoiceNumber', e.target.value);
                                      if (store) setSuggestions(store);
                                    }}
                                  />
                                </CriRibbonCell>
                                <td className="cri-td-data">
                                  <select
                                    className={`cri-cell-select ${statusClass(row.status)}`}
                                    value={row.status}
                                    onChange={(e) => updateRow(row.id, { status: e.target.value })}
                                  >
                                    {STATUS_VALUES.map((s) => (
                                      <option key={s} value={s}>
                                        {s}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="cri-td-data">
                                  <input
                                    type="date"
                                    className={`cri-cell-input cri-cell-date ${row.status === 'Paid' ? 'cri-date-paid--paid' : ''}`}
                                    value={datePaidToInputValue(row.datePaid)}
                                    onChange={(e) =>
                                      updateRow(row.id, { datePaid: inputValueToDatePaid(e.target.value) })
                                    }
                                    title="Pick date paid"
                                    aria-label="Date paid"
                                  />
                                </td>
                                <td className="cri-td-comment">
                                  <input
                                    className="cri-cell-input cri-cell-comment"
                                    value={row.comment}
                                    onChange={(e) => updateRow(row.id, { comment: e.target.value })}
                                    placeholder="Notes…"
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="cri-delete-btn"
                                    onClick={() => handleDeleteRow(row.id)}
                                    aria-label="Delete row"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                      {filteredRows.length > 0 && (
                        <tfoot>
                          <tr>
                            <td colSpan={4} className="cri-tfoot-label">
                              Total
                            </td>
                            <td className="cri-tfoot-total">{formatAmount(filteredTotal)}</td>
                            <td colSpan={6} />
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
                <CriPagination
                  totalFiltered={totalFiltered}
                  safePage={safePage}
                  totalPages={totalPages}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CarRentalInvoicing;
